import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ApiError, NetworkError } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge, OutboundStatusBadge } from '@/components/StatusBadge';
import { EvidenceGallery } from '@/components/EvidenceGallery';
import { SkuMeta, skuDisplay } from '@/components/SkuInfo';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import { useDraftActions } from '@/lib/drafts';
import { describeRequestError } from '@/lib/requests';
import { RevisionInfo } from '@/components/RevisionInfo';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). A cross-seller/manipulated `:id`
 * here resolves to `COMMON.NOT_FOUND` at the API layer (never a 403 that
 * would confirm the id exists - ADR-075 §Part B.4), and `ErrorBanner`
 * renders that as the dedicated `NotFoundState`, not a crash and not a
 * leaked title/number from the other seller's order. This is the epic's
 * named "you tried to access something not yours" denial screen for
 * orders; return detail follows the identical pattern below.
 *
 * It is also the page of a seller's DRAFT (edit / send to work / delete) and shows the execution result - the
 * order's shipment(s) with carrier and tracking - once the warehouse has one.
 */
export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const result = useApi(() => endpoints.order(id!), [id]);
  const { t, locale } = useLanguage();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelledNow, setCancelledNow] = useState(false);
  const navigate = useNavigate();
  const [sentNow, setSentNow] = useState(false);
  const reload = result.reload;
  const draft = useDraftActions('OUTBOUND', (what) => {
    if (what === 'deleted') navigate('/shipments', { replace: true });
    else {
      setSentNow(true);
      reload();
    }
  });

  async function cancelRequest(orderId: string): Promise<void> {
    setBusy(true);
    setCancelError(null);
    try {
      await endpoints.cancelOutboundRequest(orderId);
      setConfirming(false);
      setCancelledNow(true);
      result.reload();
    } catch (e) {
      setConfirming(false);
      if (e instanceof NetworkError) setCancelError(t('orderCancel.err.network'));
      else if (e instanceof ApiError && e.code === 'ORDER.CANNOT_CANCEL') {
        // the warehouse moved on meanwhile: show the fresh state
        setCancelError(t('orderCancel.err.cannot'));
        result.reload();
      } else if (e instanceof ApiError && e.code === 'ORDER.CHANNEL_ORDER_FORBIDDEN') setCancelError(t('orderCancel.err.channel'));
      else if (e instanceof ApiError && (e.code === 'COMMON.NOT_FOUND' || e.status === 404)) setCancelError(t('orderCancel.err.notFound'));
      else if (e instanceof ApiError && (e.code === 'RATE_LIMITED' || e.status === 429)) setCancelError(t('orderCancel.err.tooMany'));
      else setCancelError(t('orderCancel.err.other'));
    } finally {
      setBusy(false);
    }
  }

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;

  const { order, lines } = result.data;
  const shipments = result.data.shipments ?? [];
  const own = order.requested_in_portal === true;
  const isDraft = own && order.state === 'DRAFT';
  const cancellable = own && (order.state === 'NEW' || order.state === 'RESERVED');
  const revisable = own && (order.can_revise ?? 'NONE') !== 'NONE';
  const sentToWarehouse = own && order.state !== 'DRAFT';
  const pickingStarted = own && ['PICKING', 'PICKED', 'PACKING', 'PACKED'].includes(order.state);

  return (
    <div>
      <div className="page-header">
        <h1>{t('out.detailTitle', { number: order.number })}</h1>
        <Link to="/shipments">{t('orderDetail.backToOrders')}</Link>
      </div>

      <div className="card">
        <dl className="kv">
          <dt>{t('common.status')}</dt>
          <dd>
            <OutboundStatusBadge value={order.state} />
          </dd>
          <dt>{t('ordersPage.colChannel')}</dt>
          <dd>{order.channel_kind ?? (order.requested_in_portal ? t('requests.in.sourcePortal') : '—')}</dd>
          {order.requested_in_portal && (
            <>
              <dt>{t('requests.out.fulfilment')}</dt>
              <dd>{order.fulfilment_model === 'STANDARD' || order.fulfilment_model === 'FBS' ? t(order.fulfilment_model === 'FBS' ? 'requests.out.fulfilmentFbs' : 'requests.out.fulfilmentStandard') : t('requests.out.fulfilmentUnknown')}</dd>
            </>
          )}
          <dt>{t('orderDetail.warehouse')}</dt>
          <dd>{order.warehouse_name}</dd>
          <dt>{t('ordersPage.colDue')}</dt>
          <dd>{order.due_at ? formatDateTime(order.due_at, locale) : '—'}</dd>
          <dt>{t('dashboard.colCreated')}</dt>
          <dd>{formatDateTime(order.created_at, locale)}</dd>
        </dl>
        {order.ship_to && (
          <dl className="kv">
            <dt>{t('out.recipient')}</dt>
            <dd>{[order.ship_to.name, order.ship_to.address, order.ship_to.postalCode, order.ship_to.city, order.ship_to.country].filter(Boolean).join(', ') || '—'}</dd>
          </dl>
        )}
        {sentToWarehouse && <RevisionInfo revisionNo={order.revision_no} lastEditedAt={order.last_edited_at} lastEditedBy={order.last_edited_by} revisions={result.data.revisions} />}
        {revisable && (
          <div className="form-actions">
            <Link className="button-link" to={`/shipments/${order.id}/revise`} data-testid="revise-action">{t('revise.action')}</Link>
          </div>
        )}
        {draft.error !== null && <div className="error-banner" role="alert">{describeRequestError(t, draft.error) ?? t('drafts.err.generic')}</div>}
        {sentNow && !isDraft && <p className="order-done" role="status">{t('drafts.sentOk')}</p>}
        {isDraft && (
          <>
            <p className="kept-note">{t('drafts.hint')}</p>
            <div className="draft-actions">
              <button type="button" className="primary" disabled={draft.busyId !== null} onClick={() => void draft.send(order.id)}>
                {draft.busyId === order.id ? t('drafts.sending') : t('drafts.send')}
              </button>
              <Link className="button-link" to={`/shipments/${order.id}/edit`}>{t('drafts.edit')}</Link>
              <button type="button" className="danger" disabled={draft.busyId !== null} onClick={() => void draft.remove(order.id)}>
                {t('drafts.delete')}
              </button>
            </div>
          </>
        )}
        {cancelledNow && !cancellable && <p className="order-done" role="status">{t('orderCancel.done')}</p>}
        {cancelError !== null && <div className="error-banner" role="alert" style={{ marginTop: 12 }}>{cancelError}</div>}
        {cancellable && (
          <div className="order-cancel">
            {confirming ? (
              <div className="confirm-box" role="group" aria-label={t('orderCancel.open')}>
                <p>{t('orderCancel.confirm')}</p>
                <div className="form-actions">
                  <button type="button" className="danger" disabled={busy} onClick={() => void cancelRequest(order.id)}>
                    {busy ? t('orderCancel.busy') : t('orderCancel.yes')}
                  </button>
                  <button type="button" disabled={busy} onClick={() => setConfirming(false)}>{t('orderCancel.no')}</button>
                </div>
              </div>
            ) : (
              <button type="button" className="danger" onClick={() => { setCancelError(null); setConfirming(true); }}>
                {t('orderCancel.open')}
              </button>
            )}
          </div>
        )}
        {cancellable && <p className="kept-note">{t('drafts.keptNote')}</p>}
        {pickingStarted && <div className="order-cancel"><p className="sub">{t('orderCancel.pickingHint')}</p></div>}
      </div>

      {!isDraft && (
        <div className="card">
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('out.result')}</h2>
          {shipments.length === 0 ? (
            <p className="sub">{t('out.noResultYet')}</p>
          ) : (
            shipments.map((s) => (
              <dl className="kv" key={s.id}>
                <dt>{t('out.colNumber')}</dt>
                <dd>{s.number} · <StatusBadge value={s.state} /></dd>
                <dt>{t('shipmentsPage.colCarrier')}</dt>
                <dd>{s.carrier_kind ?? '—'}</dd>
                <dt>{t('out.resultTracking')}</dt>
                <dd>{s.carrier_ref ?? '—'}</dd>
                <dt>{t('shipmentsPage.colDeparted')}</dt>
                <dd>{s.departed_at ? formatDateTime(s.departed_at, locale) : '—'}</dd>
              </dl>
            ))
          )}
        </div>
      )}

      <div className="card">
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('orderDetail.lines')}</h2>
        <div className="table-scroll"><table>
          <thead>
            <tr>
              <th>SKU</th>
              <th>{t('inventory.colName')}</th>
              <th style={{ textAlign: 'right' }}>{t('ordersPage.colOrdered')}</th>
              <th style={{ textAlign: 'right' }}>{t('orderDetail.picked')}</th>
              <th style={{ textAlign: 'right' }}>{t('orderDetail.packed')}</th>
              <th style={{ textAlign: 'right' }}>{t('ordersPage.colShipped')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} style={{ cursor: 'default' }} className={l.cancelled_at ? 'line-cancelled' : undefined}>
                <td>{l.sku_code}{l.cancelled_at ? ` (${t('orderDetail.lineCancelled')})` : ''}</td>
                <td>{l.sku_name}<SkuMeta sku={{ code: l.sku_code, name: l.sku_name, ...skuDisplay(l) }} /></td>
                <td style={{ textAlign: 'right' }}>{formatNumber(l.qty_ordered, locale)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(l.qty_picked, locale)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(l.qty_packed, locale)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(l.qty_shipped, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {!isDraft && (
        <div className="card">
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('orderDetail.evidence')}</h2>
          <EvidenceGallery entityKind="SALES_ORDER" entityId={id!} />
        </div>
      )}
    </div>
  );
}
